import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatError, WASTE_TYPES } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Camera, Loader2, Info, Check, AlertTriangle, Sparkles, ScanBarcode, Scale } from "lucide-react";import { toast } from "sonner";

const AI_STATUS_MSG = {
  assigned: { icon: Check, tone: "success", text: "Barcode letto e associato correttamente" },
  invalid_checksum: { icon: AlertTriangle, tone: "warning", text: "Il codice letto dalla foto non è valido — verrà controllato dall'operatore" },
  not_owned: { icon: AlertTriangle, tone: "warning", text: "Il codice letto non risulta associato al tuo account — verifica manuale dell'operatore" },
  already_used: { icon: AlertTriangle, tone: "warning", text: "Il codice risulta già utilizzato per un altro conferimento" },
  not_extracted: { icon: AlertTriangle, tone: "warning", text: "Barcode non leggibile chiaramente — l'operatore lo inserirà manualmente" },
};

export default function NewSubmissionPage() {
  const nav = useNavigate();
  const cfg = useConfig();
  const [wasteType, setWasteType] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null); // { submission, ai }

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setAiResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Aggiungi una foto del conferimento");
    if (!wasteType) return toast.error("Seleziona la tipologia");

    const fd = new FormData();
    fd.append("waste_type", wasteType);
    fd.append("photo", file);

    setBusy(true);
    try {
      const { data } = await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (cfg.ai_vision_enabled) {
        setAiResult(data);
        // Toast summary
        if (data.ai?.barcode_status === "assigned" && data.ai?.weight_confidence >= (cfg.ai_min_confidence || 0.75)) {
          toast.success("Foto elaborata dall'AI — dati letti correttamente");
        } else {
          toast.warning("Foto caricata — alcuni dati non sono leggibili chiaramente");
        }
      } else {
        toast.success("Conferimento registrato — in attesa di verifica operatore");
        nav(`/submissions/${data.id}`);
      }
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const retake = () => {
    setFile(null); setPreview(null); setAiResult(null);
  };

  const proceed = () => nav(`/submissions/${aiResult.id}`);

  const ai = aiResult?.ai;

  return (
    <Layout title="Nuovo conferimento" subtitle="Scatta una sola foto che includa chiaramente peso sul display e codice a barre. Non serve trascrivere nulla: l'AI o l'operatore leggeranno i dati per te.">
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 bg-gray-50/50 hover:border-primary/40 transition-colors">
              {preview ? (
                <div className="relative">
                  <img src={preview} alt="anteprima" className="max-h-[420px] mx-auto rounded-lg" />
                  <div className="mt-4 flex justify-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={retake} className="rounded-full" data-testid="clear-photo-btn">
                      <Camera className="w-4 h-4 mr-1" /> Rifai foto
                    </Button>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center py-16 cursor-pointer" data-testid="photo-upload-label">
                  <Camera className="w-10 h-10 text-gray-400" />
                  <div className="mt-3 font-display font-semibold">Carica foto conferimento</div>
                  <div className="text-sm text-muted-foreground mt-1">Deve mostrare <strong>peso sul display</strong> e <strong>codice a barre</strong></div>
                  <input type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="photo-upload-input" />
                  <span className="mt-4 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">Scegli file</span>
                </label>
              )}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <div className="font-semibold">Come inquadrare la foto</div>
                <ol className="mt-1 space-y-0.5 list-decimal list-inside text-blue-800">
                  <li>Applica un'etichetta barcode del {cfg.comune_name} sul contenitore.</li>
                  <li>Deposita il materiale monolitico sul piano di pesatura.</li>
                  <li>Fotografa in modo che <strong>peso sul display</strong> e <strong>barcode</strong> siano ben leggibili nella stessa foto.</li>
                  <li>{cfg.ai_vision_enabled ? "L'AI leggerà automaticamente i dati; se non riesce, verificherà l'operatore." : "L'operatore leggerà peso e codice dalla foto e li registrerà."}</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Tipologia rifiuto</Label>
              <Select value={wasteType} onValueChange={setWasteType} disabled={!!aiResult}>
                <SelectTrigger data-testid="ns-waste-select" className="mt-1.5"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {WASTE_TYPES.filter((w) => (cfg.waste_types_enabled || []).includes(w.value)).map((w) => (
                    <SelectItem key={w.value} value={w.value} data-testid={`ns-waste-opt-${w.value}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: w.color }} />
                        {w.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {cfg.ai_vision_enabled && (
              <div className="p-3 rounded-lg bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-100 text-purple-900 text-xs flex gap-2">
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>Lettura AI attiva: peso e codice a barre verranno letti automaticamente dalla foto. Verrai avvisato se qualcosa non è chiaro.</div>
              </div>
            )}

            {!aiResult && (
              <Button type="submit" disabled={busy || !file || !wasteType} data-testid="ns-submit-btn" className="w-full rounded-full h-11">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Invia conferimento
              </Button>
            )}

            {aiResult && ai && (
              <div className="space-y-3" data-testid="ai-result-panel">
                <div className="font-display font-semibold text-sm">Risultato lettura AI</div>

                <ReadRow icon={Scale} label="Peso letto" value={ai.weight_kg != null ? `${ai.weight_kg.toFixed(2)} kg` : "non leggibile"} confidence={ai.weight_confidence} min={cfg.ai_min_confidence} />
                <ReadRow icon={ScanBarcode} label="Codice a barre" value={ai.barcode || "non leggibile"} confidence={ai.barcode_confidence} min={cfg.ai_min_confidence} />

                {AI_STATUS_MSG[ai.barcode_status] && (
                  <StatusPill tone={AI_STATUS_MSG[ai.barcode_status].tone} icon={AI_STATUS_MSG[ai.barcode_status].icon} text={AI_STATUS_MSG[ai.barcode_status].text} />
                )}

                {ai.notes && <div className="text-xs text-muted-foreground border-l-2 border-gray-200 pl-3">Nota AI: {ai.notes}</div>}

                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={retake} data-testid="ai-retake-btn" className="rounded-full">Rifai foto</Button>
                  <Button type="button" onClick={proceed} data-testid="ai-proceed-btn" className="rounded-full">Prosegui</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </form>
    </Layout>
  );
}

function ReadRow({ icon: Icon, label, value, confidence, min }) {
  const pct = Math.round((confidence || 0) * 100);
  const good = (confidence || 0) >= (min || 0.75);
  return (
    <div className="p-3 rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-1 flex items-baseline justify-between">
        <div className="font-mono font-semibold">{value}</div>
        <div className={`text-xs font-medium ${good ? "text-emerald-700" : "text-amber-700"}`}>{pct}%</div>
      </div>
      <div className="mt-1.5 h-1 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full ${good ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusPill({ tone, icon: Icon, text }) {
  const tones = {
    success: "bg-emerald-50 border-emerald-200 text-emerald-900",
    warning: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${tones[tone]}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>{text}</div>
    </div>
  );
}
